import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextInput, SelectInput } from "@/components/FormKit";
import { orgsApi, apiErrorMessage } from "@/api/client";
import { useAppData } from "@/context/AppDataContext";
import { toast } from "sonner";

const empty = {
  legal_name: "",
  trading_name: "",
  website: "",
  organisation_type: "",
  company_number: "",
  status: "prospect",
};

export function OrgFormDialog({ open, onOpenChange, existing, onSaved }) {
  const { enums } = useAppData();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setForm(existing ? { ...empty, ...existing } : empty);
    }
  }, [open, existing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.legal_name.trim()) {
      setError("Legal name is required");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      legal_name: form.legal_name,
      trading_name: form.trading_name || null,
      website: form.website || null,
      organisation_type: form.organisation_type || null,
      company_number: form.company_number || null,
      status: form.status,
    };
    try {
      const res = existing
        ? await orgsApi.update(existing.id, payload)
        : await orgsApi.create(payload);
      toast.success(existing ? "Organisation updated" : "Organisation created");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="org-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {existing ? "Edit organisation" : "Add organisation"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Legal name">
            <TextInput testid="org-legal-name" value={form.legal_name} onChange={set("legal_name")} placeholder="Acme Catering Ltd" />
          </Field>
          <Field label="Trading name">
            <TextInput testid="org-trading-name" value={form.trading_name || ""} onChange={set("trading_name")} placeholder="Acme" />
          </Field>
          <Field label="Website">
            <TextInput testid="org-website" value={form.website || ""} onChange={set("website")} placeholder="https://" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <SelectInput testid="org-type" value={form.organisation_type || ""} onChange={set("organisation_type")}>
                <option value="">—</option>
                {(enums?.organisation_types || []).map((t) => (
                  <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Status">
              <SelectInput testid="org-status" value={form.status} onChange={set("status")}>
                {(enums?.org_statuses || ["prospect"]).map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                ))}
              </SelectInput>
            </Field>
          </div>
          <Field label="Company number">
            <TextInput testid="org-company-number" value={form.company_number || ""} onChange={set("company_number")} placeholder="Optional" />
          </Field>
          {error && (
            <p data-testid="org-form-error" className="text-sm font-medium text-rose-600">{error}</p>
          )}
          <button
            type="submit"
            disabled={saving}
            data-testid="org-form-submit"
            className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white transition-colors hover:bg-teal-800 disabled:opacity-60"
          >
            {saving ? "Saving…" : existing ? "Save changes" : "Create organisation"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
